import React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const ModalTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 24px;
  font-weight: 600;
  color: #212121;
`;

const ExplanationText = styled.p`
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #424242;
  line-height: 1.6;
`;

const BenefitsList = styled.ul`
  margin: 0 0 20px 0;
  padding-left: 24px;
  font-size: 15px;
  color: #424242;
  line-height: 1.8;
`;

const BenefitsListItem = styled.li`
  margin-bottom: 8px;
`;

const WithoutLocationBox = styled.div`
  background-color: #f5f5f5;
  border-left: 4px solid #2e7d32;
  border-radius: 4px;
  padding: 16px;
  margin: 0 0 24px 0;
`;

const WithoutLocationTitle = styled.div`
  font-weight: 600;
  color: #212121;
  margin-bottom: 8px;
  font-size: 15px;
`;

const WithoutLocationText = styled.div`
  font-size: 14px;
  color: #616161;
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: #2e7d32;
  color: white;

  &:hover:not(:disabled) {
    background-color: #1b5e20;
  }

  &:focus {
    outline: 2px solid #2e7d32;
    outline-offset: 2px;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: #f5f5f5;
  color: #212121;

  &:hover:not(:disabled) {
    background-color: #e0e0e0;
  }

  &:focus {
    outline: 2px solid #2e7d32;
    outline-offset: 2px;
  }
`;

const LocationPermissionModal = ({ isOpen, onAllow, onSkip }) => {
  const handleOverlayClick = (e) => {
    // Don't close on overlay click - user must make a choice
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return createPortal(
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalTitle>Help Us Find Vegan Options Near You</ModalTitle>
        <ExplanationText>
          VegifyIt works best when we know your location. This helps us:
        </ExplanationText>
        <BenefitsList>
          <BenefitsListItem>
            Show you vegan options at restaurants near you
          </BenefitsListItem>
          <BenefitsListItem>
            Display accurate distances to each restaurant
          </BenefitsListItem>
          <BenefitsListItem>
            Filter results by proximity automatically
          </BenefitsListItem>
        </BenefitsList>
        <WithoutLocationBox>
          <WithoutLocationTitle>You can still use VegifyIt without location:</WithoutLocationTitle>
          <WithoutLocationText>
            Search for any city or address to explore vegan options there. Location just makes it easier to find options near you!
          </WithoutLocationText>
        </WithoutLocationBox>
        <ButtonGroup>
          <SecondaryButton type="button" onClick={onSkip}>
            Skip for Now
          </SecondaryButton>
          <PrimaryButton type="button" onClick={onAllow}>
            Allow Location
          </PrimaryButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default LocationPermissionModal;
